import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { X, Lock, ArrowRight, FileText, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const InvoicePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfUrlRef = useRef(null);
  const attachmentUrlRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicLink, setPublicLink] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [loadingAttachment, setLoadingAttachment] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        setPdfUrl(null);
        setHtmlContent('');
        try {
          const response = await api.get(`/invoices/${id}/preview`, {
            responseType: 'text'
          });
          setHtmlContent(response.data);
        } catch (previewErr) {
          // Dla PDF/skanów backend zwraca 400 – pobierz plik jako blob i wyświetl
          const fileResponse = await api.get(`/invoices/${id}/file`, {
            responseType: 'blob'
          });
          const blob = new Blob([fileResponse.data], { type: fileResponse.headers['content-type'] || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          if (pdfUrlRef.current) window.URL.revokeObjectURL(pdfUrlRef.current);
          pdfUrlRef.current = url;
          setPdfUrl(url);
        }
      } catch (err) {
        console.error('Error fetching invoice preview:', err);
        setError('Błąd podczas pobierania podglądu faktury');
      } finally {
        setLoading(false);
      }
    };

    const fetchPublicLink = async () => {
      try {
        const response = await api.get(`/invoices/${id}/public-link`);
        if (response.data && response.data.url) {
          setPublicLink(response.data.url);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          console.warn('403 Forbidden - możliwy problem z autoryzacją');
        }
      }
    };

    const fetchSignaturesAndDescriptions = async () => {
      try {
        const [sigRes, descRes] = await Promise.all([
          api.get(`/documents/invoices/${id}/signatures`).catch(() => ({ data: [] })),
          api.get(`/invoices/${id}/descriptions`).catch(() => ({ data: [] }))
        ]);
        setSignatures(Array.isArray(sigRes.data) ? sigRes.data : []);
        setDescriptions(Array.isArray(descRes.data) ? descRes.data : []);
      } catch (err) {
        console.error('Error fetching signatures/descriptions:', err);
      }
    };

    const fetchAttachments = async () => {
      try {
        const res = await api.get(`/invoices/${id}/attachments`);
        const list = Array.isArray(res.data) ? res.data : [];
        setAttachments(list);
      } catch (err) {
        console.warn('Błąd pobierania załączników:', err?.response?.status || err?.message);
        setAttachments([]);
      }
    };

    if (id) {
      fetchPreview();
      fetchPublicLink();
      fetchSignaturesAndDescriptions();
      fetchAttachments();
    }
    return () => {
      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      if (attachmentUrlRef.current) {
        window.URL.revokeObjectURL(attachmentUrlRef.current);
        attachmentUrlRef.current = null;
      }
    };
  }, [id]);

  const handleAttachmentClick = async (attachment) => {
    setLoadingAttachment(true);
    setSelectedAttachment(attachment);
    setAttachmentPreviewUrl(null);
    try {
      const response = await api.get(`/invoices/attachments/${attachment.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      if (attachmentUrlRef.current) window.URL.revokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = blobUrl;
      setAttachmentPreviewUrl(blobUrl);
    } catch (err) {
      console.error('Error fetching attachment:', err);
      toast.error('Nie udało się pobrać załącznika');
      setSelectedAttachment(null);
    } finally {
      setLoadingAttachment(false);
    }
  };

  const handleCloseAttachment = () => {
    setSelectedAttachment(null);
    setAttachmentPreviewUrl(null);
    if (attachmentUrlRef.current) {
      window.URL.revokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = null;
    }
  };

  const handleDownloadAttachment = () => {
    if (!selectedAttachment || !attachmentPreviewUrl) return;
    const a = document.createElement('a');
    a.href = attachmentPreviewUrl;
    a.download = selectedAttachment.originalFileName || `zalacznik-${selectedAttachment.id}`;
    a.click();
  };

  const handleClose = () => {
    // Jeśli otwarte w nowym oknie, zamknij je
    if (window.opener) {
      window.close();
    } else {
      // Jeśli otwarte w tej samej karcie, wróć do poprzedniej strony
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Błąd</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header z przyciskiem zamknięcia i załącznikami */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-4 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900">Podgląd faktury</h1>
            <span className="text-sm text-gray-500">ID: {id}</span>
            {attachments.length > 0 && (
              <div className="flex items-center gap-2 ml-2 flex-wrap">
                <span className="text-sm text-gray-600">Załączniki:</span>
                {attachments.map((att) => (
                  <button
                    key={att.id}
                    onClick={() => handleAttachmentClick(att)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 rounded border border-blue-200 whitespace-nowrap"
                    title={att.originalFileName}
                  >
                    <FileText className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate max-w-[120px]">{att.originalFileName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4 flex-shrink-0">
          {publicLink ? (
            <button
              onClick={async () => {
                const urlToCopy = publicLink;
                
                // Metoda 1: Nowoczesne Clipboard API (wymaga HTTPS lub localhost)
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  try {
                    await navigator.clipboard.writeText(urlToCopy);
                    toast.success('Link publiczny skopiowany do schowka!');
                    return;
                  } catch (err) {
                    console.warn('Clipboard API failed, trying fallback:', err);
                  }
                }
                
                // Metoda 2: Fallback - tworzenie tymczasowego inputa
                try {
                  const textArea = document.createElement('textarea');
                  textArea.value = urlToCopy;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-999999px';
                  textArea.style.top = '-999999px';
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  
                  const successful = document.execCommand('copy');
                  document.body.removeChild(textArea);
                  
                  if (successful) {
                    toast.success('Link publiczny skopiowany do schowka!');
                  } else {
                    throw new Error('execCommand failed');
                  }
                } catch (err) {
                  console.error('All copy methods failed:', err);
                  // Ostatnia deska ratunku - pokaż link do ręcznego skopiowania
                  const userConfirmed = window.prompt(
                    'Nie można automatycznie skopiować linku. Skopiuj go ręcznie:\n\n' + urlToCopy
                  );
                  if (userConfirmed !== null) {
                    toast.success('Link wyświetlony do skopiowania');
                  }
                }
              }}
              className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
              title="Kopiuj link publiczny (dostępny bez logowania)"
            >
              Kopiuj link publiczny
            </button>
          ) : (
            <button
              onClick={async () => {
                // Jeśli nie ma jeszcze publicznego linku, spróbuj go pobrać
                try {
                  const response = await api.get(`/invoices/${id}/public-link`);
                  if (response.data && response.data.url) {
                    setPublicLink(response.data.url);
                    const urlToCopy = response.data.url;
                    
                    // Skopiuj link
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      try {
                        await navigator.clipboard.writeText(urlToCopy);
                        toast.success('Link publiczny skopiowany do schowka!');
                        return;
                      } catch (err) {
                        console.warn('Clipboard API failed, trying fallback:', err);
                      }
                    }
                    
                    // Fallback
                    const textArea = document.createElement('textarea');
                    textArea.value = urlToCopy;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    
                    if (successful) {
                      toast.success('Link publiczny skopiowany do schowka!');
                    } else {
                      window.prompt('Skopiuj link ręcznie:', urlToCopy);
                    }
                  } else {
                    toast.error('Nie udało się wygenerować publicznego linku');
                  }
                } catch (err) {
                  console.error('Error generating public link:', err);
                  toast.error('Nie udało się wygenerować publicznego linku');
                }
              }}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              title="Generuj i kopiuj link publiczny"
            >
              Kopiuj link publiczny
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
          </div>
        </div>
      </div>

      {/* Zawartość faktury */}
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {pdfUrl ? (
            <iframe
              title="Podgląd faktury (PDF)"
              src={pdfUrl}
              className="w-full border-0"
              style={{ minHeight: '60vh' }}
            />
          ) : (
            <iframe
              title="Podgląd faktury"
              srcDoc={htmlContent}
              className="w-full border-0"
              style={{ minHeight: '60vh' }}
            />
          )}
        </div>

        {/* Podpisy elektroniczne – historia podpisów dokumentu (jak na liście faktur) */}
        {signatures.length > 0 && (() => {
          const uniqueSignatures = new Map();
          signatures.forEach(sig => {
            if (sig.user && sig.signedAt) {
              const key = sig.signatureGroupId || `${sig.user.id}_${new Date(sig.signedAt).toISOString().substring(0, 19)}`;
              if (!uniqueSignatures.has(key)) uniqueSignatures.set(key, sig);
            }
          });
          const sortedSignatures = Array.from(uniqueSignatures.values()).sort(
            (a, b) => new Date(a.signedAt) - new Date(b.signedAt)
          );
          const formatCompactDate = (date) => {
            const d = new Date(date);
            return `${d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' })}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
          };
          return (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Lock className="h-5 w-5 text-gray-600" />
                <h2 className="text-base font-semibold text-gray-900">Podpisy elektroniczne – historia podpisów</h2>
              </div>
              <div className="p-4 overflow-x-auto">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {sortedSignatures.map((sig, idx) => {
                    let description = descriptions.find(d => d.documentSignatureId && String(d.documentSignatureId) === String(sig.id));
                    if (!description && sig.user?.id) {
                      description = descriptions.find(d => {
                        if (!d.userId || String(d.userId) !== String(sig.user.id)) return false;
                        if (d.documentSignatureId && String(d.documentSignatureId) !== String(sig.id)) return false;
                        const timeDiff = Math.abs(new Date(d.createdAt || d.updatedAt || 0) - new Date(sig.signedAt));
                        return timeDiff < 900000;
                      });
                    }
                    if (!description && sig.user?.id) {
                      const userDescriptions = descriptions.filter(d =>
                        d.userId && String(d.userId) === String(sig.user.id) && (!d.documentSignatureId || d.documentSignatureId === sig.id)
                      );
                      if (userDescriptions.length > 0) {
                        const sigTime = new Date(sig.signedAt).getTime();
                        let closest = null;
                        let minDiff = Infinity;
                        userDescriptions.forEach(d => {
                          const diff = Math.abs(new Date(d.createdAt || d.updatedAt || 0).getTime() - sigTime);
                          if (diff < 3600000 && diff < minDiff) {
                            minDiff = diff;
                            closest = d;
                          }
                        });
                        if (closest) description = closest;
                      }
                    }
                    const descToShow = description;
                    const isAttachmentSignature = sig.attachmentId != null;
                    const isRealizacjiOnly = descToShow && descToShow.zatwierdzonoDo === 'REALIZACJI' && !descToShow.metricalChecked && !descToShow.preliminaryCheckDone && !descToShow.formalnoRachunkowoChecked && !isAttachmentSignature;
                    const bgColor = isAttachmentSignature ? 'bg-blue-50' : (isRealizacjiOnly ? 'bg-emerald-50' : 'bg-green-50');
                    const borderColor = isAttachmentSignature ? 'border-blue-200' : (isRealizacjiOnly ? 'border-emerald-300' : 'border-green-200');
                    const iconColor = isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600');
                    const arrowColor = isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600');
                    const userName = sig.user ? `${sig.user.firstName || ''} ${sig.user.lastName || ''}`.trim() || sig.user.email || 'Nieznany' : 'Nieznany';
                    const dateStr = formatCompactDate(sig.signedAt);
                    return (
                      <React.Fragment key={sig.signatureGroupId || sig.id || idx}>
                        <div className={`${bgColor} border ${borderColor} rounded-md p-2.5 flex-shrink-0 min-w-[160px] sm:min-w-[180px] max-w-[220px]`}>
                          <div className="flex items-start gap-1.5">
                            <Lock className={`h-3.5 w-3.5 ${iconColor} mt-0.5 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              {descToShow && descToShow.metricalChecked ? (
                                <>
                                  <div className="font-semibold text-green-700 text-xs">SPRAWDZONO MERYTORYCZNYM</div>
                                  <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                </>
                              ) : descToShow && descToShow.preliminaryCheckDone ? (
                                <>
                                  <div className="font-semibold text-blue-700 text-xs">WSTĘPNA KONTROLA</div>
                                  <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                </>
                              ) : descToShow && descToShow.formalnoRachunkowoChecked ? (
                                <>
                                  <div className="font-semibold text-purple-700 text-xs">SPRAWDZONO FORMALNO-RACHUNKOWO</div>
                                  <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                  {descToShow.zatwierdzonoDo && descToShow.zatwierdzonoDo !== 'REALIZACJI' && (
                                    <div className="mt-1 pt-1 border-t border-green-300 text-[10px]">
                                      DO: <strong className="text-purple-700">{descToShow.zatwierdzonoDo}</strong>
                                    </div>
                                  )}
                                  {descToShow.kwota && (
                                    <div className="text-[10px] text-gray-700 mt-0.5">
                                      <strong>{descToShow.kwota} PLN</strong>
                                      {descToShow.kwotaSlownie && (
                                        <span className="ml-1 text-gray-500 text-[9px]">({descToShow.kwotaSlownie.substring(0, 35)}…)</span>
                                      )}
                                    </div>
                                  )}
                                </>
                              ) : descToShow && descToShow.zatwierdzonoDo === 'REALIZACJI' ? (
                                <>
                                  <div className="font-semibold text-red-700 text-xs">ZATWIERDZONO DO REALIZACJI</div>
                                  <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                </>
                              ) : isAttachmentSignature ? (
                                <>
                                  <div className="font-semibold text-blue-700 text-xs">ZAŁĄCZNIK</div>
                                  <div className="text-gray-600 text-xs mt-0.5 truncate" title={sig.attachmentFileName}>{sig.attachmentFileName || 'załącznik'}</div>
                                  <div className="text-gray-600 text-[10px] mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                </>
                              ) : (
                                <>
                                  <div className="font-semibold text-gray-700 text-xs">PODPISANO</div>
                                  <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                                  <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                </>
                              )}
                              {!isAttachmentSignature && descToShow && descToShow.formalnoRachunkowoChecked && descToShow.zatwierdzonoDo === 'REALIZACJI' && (
                                <div className="mt-1.5 pt-1.5 border-t border-emerald-400">
                                  <div className="text-[10px] font-semibold text-purple-700">✓ ZATWIERDZONO DO REALIZACJI</div>
                                </div>
                              )}
                              {!isAttachmentSignature && descToShow && descToShow.invoicePaymentType && (
                                <div className="mt-1 pt-1 border-t border-gray-300 text-[10px] text-gray-700">
                                  <span className="font-medium">Typ płatności: </span>
                                  {descToShow.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : descToShow.invoicePaymentType === 'TRANSFER' ? 'Przelew' : descToShow.invoicePaymentType}
                                </div>
                              )}
                              {!isAttachmentSignature && descToShow && descToShow.description && (
                                <div className="mt-1.5 pt-1.5 border-t border-gray-300">
                                  <div className="text-[10px] text-gray-700 truncate" title={descToShow.description}>
                                    {descToShow.description.length > 45 ? `${descToShow.description.substring(0, 45)}…` : descToShow.description}
                                  </div>
                                  {descToShow.costCenter && (
                                    <span className="block mt-0.5 font-semibold text-yellow-700 text-[9px]">OŚR. KOSZTÓW: {descToShow.costCenter}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {idx < sortedSignatures.length - 1 && (
                          <div className="flex items-center justify-center flex-shrink-0 px-0.5">
                            <ArrowRight className={`h-4 w-4 ${arrowColor}`} />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Modal podglądu załącznika */}
        {selectedAttachment && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={handleCloseAttachment}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b">
                <span className="font-medium text-gray-900 truncate flex-1">{selectedAttachment.originalFileName}</span>
                <div className="flex gap-2">
                  <button onClick={handleDownloadAttachment} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Pobierz">
                    <Download className="h-5 w-5" />
                  </button>
                  <button onClick={handleCloseAttachment} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Zamknij">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-4 bg-gray-50 min-h-[400px]">
                {loadingAttachment ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner />
                  </div>
                ) : attachmentPreviewUrl ? (
                  (() => {
                    const ft = selectedAttachment.fileType || '';
                    const fn = (selectedAttachment.originalFileName || '').toLowerCase();
                    const isPdf = ft.includes('pdf') || fn.endsWith('.pdf');
                    const isImage = ft.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(fn);
                    if (isImage) {
                      return <img src={attachmentPreviewUrl} alt={selectedAttachment.originalFileName} className="max-w-full h-auto mx-auto" />;
                    }
                    if (isPdf) {
                      return <iframe src={attachmentPreviewUrl} title="Podgląd" className="w-full border-0" style={{ minHeight: '60vh' }} />;
                    }
                    return (
                      <div className="flex flex-col items-center justify-center p-8">
                        <FileText className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-600 mb-4">Ten typ pliku nie może być wyświetlony w przeglądarce.</p>
                        <button onClick={handleDownloadAttachment} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2">
                          <Download className="h-4 w-4" /> Pobierz plik
                        </button>
                      </div>
                    );
                  })()
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePreview;
